import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faFloppyDisk,
  faPen,
  faRepeat,
  faTrash,
  faTrashCanArrowUp,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "react-bootstrap";
import SubmitButtonWithSpinner from "../pages/utils/SubmitButtonWithSpinner";
import IconButton from "./IconButton";

/**
 * Action buttons for a section row in edit mode.
 * Optional saveLabel/cancelLabel: use text instead of icons (e.g. "Sauvegarder" / "Annuler");
 * when used, buttons match SignatureModal/BaseFormModal style (primary + secondary).
 */
export function EditModeButtons({ onSave, onCancel, saveLabel, cancelLabel }) {
  const useLabelStyle = Boolean(saveLabel ?? cancelLabel);
  return (
    <>
      {cancelLabel ? (
        <Button variant="secondary" onClick={onCancel}>
          {cancelLabel}
        </Button>
      ) : (
        <IconButton icon={faXmark} variant="link" onClick={onCancel} />
      )}
      <SubmitButtonWithSpinner
        submitFct={onSave}
        variant={useLabelStyle ? "primary" : "link"}
      >
        {saveLabel ?? <FontAwesomeIcon icon={faFloppyDisk} />}
      </SubmitButtonWithSpinner>
    </>
  );
}

/**
 * Action buttons for a deleted section row
 */
export function DeletedRowButtons({ onRestore, onHardDelete }) {
  return (
    <>
      <SubmitButtonWithSpinner submitFct={onRestore} variant="link">
        <FontAwesomeIcon icon={faTrashCanArrowUp} />
      </SubmitButtonWithSpinner>
      <Button variant="link">
        <FontAwesomeIcon
          icon={faTrash}
          className="text-danger"
          onClick={onHardDelete}
        />
      </Button>
    </>
  );
}

/**
 * Action buttons for a normal (non-deleted, non-editing) section row
 */
export function NormalRowButtons({ onEdit, onDelete, onReset }) {
  return (
    <>
      <IconButton icon={faPen} variant="link" onClick={onEdit} />
      <SubmitButtonWithSpinner
        submitFct={onDelete}
        variant="link"
        className="text-danger"
      >
        <FontAwesomeIcon icon={faTrash} />
      </SubmitButtonWithSpinner>
      <IconButton icon={faRepeat} variant="link" onClick={onReset} />
    </>
  );
}

/**
 * History button (used in all row states) with optional badge for modification count.
 * Disabled when there are no modifications. Wrapper has fixed min-width to keep alignment when badge is absent.
 */
export function HistoryButton({ onClick, historyCount = 0 }) {
  const hasHistory = historyCount > 0;
  return (
    <span
      className="position-relative d-inline-block"
      style={{ minWidth: "1.75rem" }}
    >
      <IconButton
        icon={faClockRotateLeft}
        variant="link"
        onClick={hasHistory ? onClick : undefined}
        disabled={!hasHistory}
      />
      {hasHistory && (
        <span
          className="position-absolute bottom-0 translate-middle badge rounded-pill bg-primary"
          style={{ fontSize: "0.5rem", marginLeft: "-0.3rem" }}
        >
          {historyCount > 99 ? "99+" : historyCount}
        </span>
      )}
    </span>
  );
}
